import axiosApiInstance from '../../../api/axiosClient';
import { message } from 'antd';

// Interface for uploaded file data
export interface UploadedFile {
    uid: string;
    name: string;
    type: string;
    fileType: 'image' | 'audio' | 'other';
    size: number;
    url?: string;
    duration?: number;
    processingStatus?: string;
    originFileObj?: File;
    validationResult?: ValidationResult;
    metadata?: { [key: string]: string };
}

// Interface for validation result
export interface ValidationResult {
    isValid: boolean;
    issues: string[];
    platformResults?: {
        [platform: string]: PlatformValidationResult;
    };
}

// Interface for platform-specific validation result
export interface PlatformValidationResult {
    isValid: boolean;
    issues: string[];
}

/**
 * Fetches and processes the list of uploaded files for a release
 * @param folderName - The release ID or folder name
 * @param setUploadedFiles - Function to set the uploaded files state
 * @param setRefreshing - Optional function to set the refreshing state
 * @returns Promise<UploadedFile[]> - The processed files
 */
export const fetchReleaseFiles = async (
    folderName: string,
    setUploadedFiles: (files: UploadedFile[]) => void,
    setRefreshing?: (refreshing: boolean) => void
): Promise<UploadedFile[]> => {
    if (setRefreshing) {
        setRefreshing(true);
    }

    try {
        const response = await axiosApiInstance.get(`/api/products/releases/${folderName}/list-uploaded-files`);

        if (response.data && response.data.files && Array.isArray(response.data.files)) {
            const filesArray = response.data.files;
            const processedFiles = filesArray.map((file: any) => {
                let fileType = 'other';
                if (file.type?.startsWith('image/')) {
                    fileType = 'image';
                } else if (file.type?.startsWith('audio/')) {
                    fileType = 'audio';
                }

                return {
                    uid: file.uid || `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    name: file.name,
                    type: file.type || '',
                    fileType,
                    size: file.size || 0,
                    status: 'done',
                    url: file.url,
                    duration: file.duration,
                    processingStatus: file.processingStatus || '',
                    validationResult: file.validationResult || null,
                    metadata: file.metadata || {}
                };
            });

            setUploadedFiles(processedFiles as UploadedFile[]);
            return processedFiles as UploadedFile[];
        } else {
            setUploadedFiles([]);
            return [];
        }
    } catch (err) {
        console.error('Error fetching release files:', err);
        message.error('Failed to fetch release files');
        setUploadedFiles([]);
        return [];
    } finally {
        if (setRefreshing) {
            setRefreshing(false);
        }
    }
};

/**
 * Deletes a file from a release
 * @param folderName - The release ID or folder name
 * @param fileName - The name of the file to delete
 * @returns Promise<boolean> - Whether the deletion was successful
 */
export const deleteReleaseFile = async (
    folderName: string,
    fileName: string
): Promise<boolean> => {
    try {
        const response = await axiosApiInstance.post('/api/products/releases/delete-object', {
            folderName,
            fileName
        });

        if (response.status === 200) {
            message.success(`File ${fileName} deleted successfully`);
            return true;
        } else {
            throw new Error('Failed to delete file');
        }
    } catch (err) {
        console.error('Error deleting file:', err);
        message.error(`Failed to delete file ${fileName}`);
        return false;
    }
};

/**
 * Strips double (or more) consecutive spaces to a single space in filename
 * @param file - The file to process
 * @returns File - A new file with processed filename or the original file if no changes needed
 */
export const stripConsecutiveSpacesInFilename = (file: File): File => {
    try {
        // Get the original filename
        const originalName = file.name;

        // Replace multiple spaces with a single space in the filename
        const processedName = originalName.replace(/ {2,}/g, ' ');

        // If the filename hasn't changed, return the original file
        if (originalName === processedName) {
            return file;
        }

        console.log(`Processed filename: "${originalName}" -> "${processedName}"`);

        // Create a new file with the processed filename
        const processedFile = new File([file], processedName, { type: file.type });

        return processedFile;
    } catch (error) {
        console.error('Error processing filename:', error);
        // Return the original file if there's an error
        return file;
    }
};
